import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import SEO from "@/components/SEO";
import { LAUNCH_CHECKLIST } from "@/data/siteContent";

const TEXT_FIELDS = [
  ["phone_display", "Phone number (display)"],
  ["npn", "National Producer Number (NPN)"],
  ["states_served_text", "States served (short text)"],
  ["business_hours", "Business hours"],
  ["response_time", "Expected response time"],
  ["announcement", "Announcement bar text"],
  ["hero_headline", "Home hero headline"],
  ["business_address", "Business address"],
];

export default function Admin() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [leads, setLeads] = useState([]);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("settings");

  useEffect(() => {
    api.get("/auth/me").catch(() => navigate("/admin/login"));
    api.get("/site-config").then((res) => {
      setConfig(res.data);
    }).catch(() => {});
    api.get("/admin/leads").then((res) => setLeads(res.data)).catch(() => {});
  }, [navigate]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site-config", config);
      toast.success("Site settings saved.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSaving(false);
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  if (!config) return <div className="container-site py-20" data-testid="admin-loading">Loading…</div>;

  return (
    <>
      <SEO title="Admin | 1907 Insurance Group" description="Site administration." />
      <section className="py-14 md:py-20" data-testid="admin-page">
        <div className="container-site max-w-4xl">
          <div className="flex items-center justify-between gap-4">
            <h1 className="text-3xl sm:text-4xl tracking-tight">Site Administration</h1>
            <button onClick={logout} className="btn-outline !py-2.5 !text-base" data-testid="admin-logout-button">Sign Out</button>
          </div>

          <div className="mt-8 flex gap-2" role="tablist">
            {[["settings", "Business Settings"], ["carriers", "Carriers"], ["leads", `Leads (${leads.length})`], ["checklist", "Launch Checklist"]].map(([key, label]) => (
              <button key={key} role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
                className={`rounded-md px-4 py-2.5 font-semibold min-h-[44px] transition-colors ${tab === key ? "bg-navy text-white" : "bg-white border border-navy-100 text-navy hover:bg-navy-50"}`}
                data-testid={`admin-tab-${key}`}>
                {label}
              </button>
            ))}
          </div>

          {tab === "settings" && (
            <div className="mt-8 rounded-2xl border border-navy-100 bg-white p-8 shadow-sm" data-testid="admin-settings-panel">
              <div className="grid gap-5 sm:grid-cols-2">
                {TEXT_FIELDS.map(([key, label]) => (
                  <div key={key} className={["business_address", "announcement", "hero_headline"].includes(key) ? "sm:col-span-2" : ""}>
                    <label className="field-label" htmlFor={`adm-${key}`}>{label}</label>
                    <input id={`adm-${key}`} className="field-input" value={config[key] ?? ""}
                      onChange={(e) => setConfig({ ...config, [key]: e.target.value })} data-testid={`admin-field-${key}`} />
                  </div>
                ))}
              </div>
              <button onClick={save} className="btn-accent mt-6" disabled={saving} data-testid="admin-save-button">
                {saving ? "Saving…" : "Save Settings"}
              </button>
            </div>
          )}

          {tab === "carriers" && (
            <div className="mt-8 rounded-2xl border border-navy-100 bg-white p-8 shadow-sm" data-testid="admin-carriers-panel">
              <p className="text-mutedink">Toggle carriers on or off. Disabled carriers disappear from the public site without changing the layout.</p>
              <ul className="mt-6 space-y-3">
                {(config.carriers || []).map((c, i) => (
                  <li key={c.name} className="flex items-center justify-between gap-4 rounded-lg border border-navy-100 p-4">
                    <span className="flex items-center gap-4">
                      <img src={c.logo} alt="" className="h-8 w-auto object-contain" />
                      <span className="font-semibold text-navy">{c.name}</span>
                    </span>
                    <label className="flex items-center gap-2 font-semibold text-navy cursor-pointer min-h-[44px]">
                      <input type="checkbox" className="h-5 w-5 accent-[#0B1B3D]" checked={c.enabled !== false}
                        onChange={(e) => {
                          const next = [...config.carriers];
                          next[i] = { ...next[i], enabled: e.target.checked };
                          setConfig({ ...config, carriers: next });
                        }} data-testid={`admin-carrier-toggle-${i}`} />
                      Visible
                    </label>
                  </li>
                ))}
              </ul>
              <button onClick={save} className="btn-accent mt-6" disabled={saving} data-testid="admin-carriers-save-button">
                {saving ? "Saving…" : "Save Carriers"}
              </button>
            </div>
          )}

          {tab === "leads" && (
            <div className="mt-8 space-y-4" data-testid="admin-leads-panel">
              {leads.length === 0 && <p className="text-mutedink">No leads yet.</p>}
              {leads.map((l) => (
                <div key={l.lead_id} className="rounded-xl border border-navy-100 bg-white p-6 shadow-sm" data-testid={`admin-lead-${l.lead_id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-semibold text-navy capitalize">{l.type} — {l.data.first_name || l.data.name} {l.data.last_name || ""}</span>
                    <span className="text-sm text-mutedink">{new Date(l.submitted_at).toLocaleString()}</span>
                  </div>
                  <p className="mt-2 text-mutedink">{l.data.phone} • {l.data.email} {l.data.state ? `• ${l.data.state}` : ""}</p>
                  {l.data.message && <p className="mt-2 text-mutedink">{l.data.message}</p>}
                  <p className="mt-2 text-sm text-mutedink/70">Consent: {l.consent ? "Yes" : "No"} • Source: {l.source_page || "—"}</p>
                </div>
              ))}
            </div>
          )}

          {tab === "checklist" && (
            <div className="mt-8 rounded-2xl border border-gold/50 bg-gold/10 p-8" data-testid="admin-checklist-panel">
              <h2 className="text-2xl tracking-tight">Internal Launch Checklist</h2>
              <ul className="mt-5 space-y-3">
                {LAUNCH_CHECKLIST.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-navy">
                    <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-gold-dark" aria-hidden="true" /> {item}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
